import { Link } from 'react-router-dom';
import { MapPin, Building2, GraduationCap, ExternalLink, QrCode } from 'lucide-react';
import { EntityBadge } from '../common/EntityBadge';
import { CopyButton } from '../common/CopyButton';
import type { PartnerSearchResult } from '../../types';

interface ResultCardProps {
  result: PartnerSearchResult;
}

export function ResultCard({ result }: ResultCardProps) {
  const detailPath = result.entityType === 'business'
    ? `/business/${result.id}`
    : `/trainer/${result.id}`;

  const qrPath = result.entityType === 'business'
    ? `/qr/business/${result.id}?name=${encodeURIComponent(result.displayName)}`
    : `/qr/trainer/${result.id}?name=${encodeURIComponent(result.displayName)}`;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <EntityBadge type={result.entityType} />

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">
                {result.displayName}
              </h3>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
                {result.companyName && (
                  <span>{result.companyName}</span>
                )}
                {result.nip && (
                  <span>NIP: {result.nip}</span>
                )}
              </div>
            </div>

            {result.hasTPay && (
              <span className="shrink-0 text-xs font-medium bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full">
                TPay
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <MapPin size={14} />
              {result.city}
            </span>
            {result.entityType === 'business' && result.facilitiesCount != null && (
              <span className="flex items-center gap-1">
                <Building2 size={14} />
                {result.facilitiesCount} {result.facilitiesCount === 1 ? 'obiekt' : 'obiektow'}
              </span>
            )}
            {result.entityType === 'trainer' && (
              <>
                {result.specializations && result.specializations.length > 0 && (
                  <span className="flex items-center gap-1">
                    <GraduationCap size={14} />
                    {result.specializations.join(', ')}
                  </span>
                )}
                {result.trainingsCount != null && (
                  <span>{result.trainingsCount} treningow</span>
                )}
                {result.experienceYears != null && (
                  <span>{result.experienceYears} lat doswiadczenia</span>
                )}
              </>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-4">
            <Link
              to={detailPath}
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
            >
              <ExternalLink size={14} />
              Szczegoly
            </Link>
            <CopyButton text={result.deepLink} label="Kopiuj link" />
            <Link
              to={qrPath}
              target="_blank"
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            >
              <QrCode size={14} />
              QR kod
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
